import { Dispatch, FC, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { Action } from 'redux';

import lang from '../../../lib/language';
import { EpicMoversParamType, TopGainerAndLoserData } from '../../../lib/models/fundamental/types';
import { RootState } from '../../../lib/store';
import { epicMoversFilterOption, setEpicMoversFilterOption } from '../../../lib/store/discover/epicMoversState';
import { getEpicMovers as getEpicMoversCall } from '../../../lib/store/fundamental/index';
import { epicMoversOptionToString, getEpicMoversOptionByStringValue } from '../../../lib/store/fundamental/mappers';
import { FundamentalCall } from '../../../lib/store/fundamental/types';
import TradePriceCache from '../../../lib/store-util/TradePriceCache';
import { getPriceColorByDirection } from '../../../lib/util/ChartingHelpers';
import { formatCurrency, getLimitedText } from '../../../lib/util/DataHelpers';
import { isCallStatusReady } from '../../../lib/util/error-handling/StatusByCallHelpers';
import { calculatePriceMovement } from '../../../lib/util/lastPriceChangeHelpers';
import { PriceMovement } from '../../../lib/util/types';

import { ClickableDiv, CompanyLogo, DiscoverInternalTable, GoBackButton } from '../../../components';
import ChartLine from '../../../components/Chart/ChartLine/ChartLine';
import { useIsMobile } from '../../../hooks';
import { UI_ROUTES } from '../../../util/routes';
import PriceAndChange from '../components/PriceAndChange/PriceAndChange';
import { EPIC_MOVERS_SYMBOL_NAME_LIMIT } from '../EpicMovers.constants';

import SkeletonLoaderRow from './SkeletonLoaderRow';

import styles from './EpicMoversInternal.module.scss';

type RowProps = { item: TopGainerAndLoserData; displayName: string; logo: string; url: string };

let priceMovement: PriceMovement;

const TableRow: FC<RowProps> = (props: RowProps): JSX.Element => {
  const { item, displayName, logo, url } = props;
  const navigate = useNavigate();
  const { symbol } = item;
  const { currentPrice, changePercent, direction } = TradePriceCache.use(symbol, 'SymbolTradeInfoCard');
  const color = getPriceColorByDirection(direction);
  priceMovement = calculatePriceMovement(currentPrice, symbol);

  return (
    <tr
      className={styles.row}
      key={symbol}
      onClick={() => navigate(`${UI_ROUTES.symbol}/${symbol}`)}
    >
      <td>
        <div className={styles.wrapperCompany}>
          {(url || logo) && (
            <CompanyLogo url={url} logo={logo} symbol={symbol} />
          )}
          <div className={styles.companyInfo}>
            {!symbol || !displayName ? (
              <>
                <div className={classNames(styles.loadingComponent, styles.loadingComponentMargin)} />
                <div className={styles.loadingComponent} style={{ width: 134 }} />
              </>
            ) : (
              <div className={styles.nameAndSymbol}>
                <div className={styles.symbol}>{symbol}</div>
                <div className={styles.name}>{displayName}</div>
              </div>
            )}
          </div>
        </div>
      </td>
      <td className={styles.columnChart}><ChartLine symbol={symbol} /></td>
      <td>
        <div className={styles.lastPrice}>
          <span className={classNames(styles.currentPrice, {
            [styles.noPriceChange]: priceMovement === 'equal',
            [styles.negativeChange]: priceMovement === 'down',
            [styles.positiveChange]: priceMovement === 'up',
          })}
          >
            {formatCurrency(currentPrice ?? item.price)}
          </span>
        </div>
      </td>
      <td>
        <span style={{ color }}>
          (
          {direction}
          {changePercent}
          )
        </span>
      </td>
    </tr>
  );
};

const EpicMoversInternal = (props: ReduxProps) => {
  const { getEpicMovers, isEpicMoversReady, epicMoversData, companyLogoAndName } = props;
  const { responseData } = epicMoversData;
  const { topGainers, topLosers } = responseData ?? {};

  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const nameLimit = isMobile ? 14 : EPIC_MOVERS_SYMBOL_NAME_LIMIT;

  const location = useLocation();
  const { state } = location;

  const [activeTabIndex, setActiveTabIndex] = useState<number>(
    state?.isTopGainersSelected ? 0 : 1,
  );
  const optionValue = epicMoversFilterOption.get();

  useEffect(() => {
    if (isMobile) {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For other browsers
    }
  }, [isMobile]);

  useEffect(() => {
    getEpicMovers({
      range: optionValue,
    });
  }, [getEpicMovers, optionValue]);

  const handleFilterOption = (option: number[], valueOption: string) => {
    const newOptionValue = getEpicMoversOptionByStringValue(valueOption);
    setEpicMoversFilterOption(newOptionValue);
    getEpicMovers({ range: newOptionValue });
  };

  const onTabChange = (index: number) => {
    setActiveTabIndex(index);
  };

  const renderList = (activeTabIndex === 0 ? topGainers : topLosers).length === 0
    ? (
      <>
        <SkeletonLoaderRow />
        <SkeletonLoaderRow />
        <SkeletonLoaderRow />
        <SkeletonLoaderRow />
      </>
    )
    : (activeTabIndex === 0 ? topGainers : topLosers).map((item: TopGainerAndLoserData) => {
      const logo = companyLogoAndName[item.symbol]?.logo;
      const url = companyLogoAndName[item.symbol]?.url ?? item.url;
      const displayName = getLimitedText(item.name ?? companyLogoAndName[item.symbol]?.name, nameLimit);

      if (isMobile) {
        return (
          <ClickableDiv
            key={item.symbol}
            onClick={() => navigate(`${UI_ROUTES.symbol}/${item.symbol}`)}
            className={styles.wrapperCompanyMob}
          >
            <div className={styles.left}>
              <div className={styles.logoWrapper}>
                {(url || logo) && (
                  <CompanyLogo url={url} logo={logo} symbol={item.symbol} />
                )}
              </div>
              <div className={styles.companyInfo}>
                <p className={styles.symbol}>{item.symbol}</p>
                <p className={styles.displayTime}>
                  {displayName}
                </p>
              </div>
            </div>
            <div className={styles.mobileChart}>
              <ChartLine symbol={item.symbol} />
            </div>
            <div className={styles.right}>
              <PriceAndChange item={item} isReduction={activeTabIndex === 1} isLoading={false} />
            </div>
          </ClickableDiv>
        );
      }

      return (
        <TableRow
          key={item.symbol}
          item={item}
          url={url}
          logo={logo}
          displayName={displayName}
        />
      );
    });

  return (
    <div className={isMobile ? classNames(styles.wrapper, styles.noMargin) : styles.wrapper}>
      {!isMobile && (
        <div className={styles.goBackButton}>
          <GoBackButton />
        </div>
      )}
      <div className={styles.title}>
        {lang.commonEpicMoversToday()}
      </div>
      <div className={styles.description}>
        {lang.commonEpicMoversDescription()}
      </div>
      <div className={styles.table}>
        <DiscoverInternalTable
          fullWidthButtons
          hasSecondTable
          isLoading={!isEpicMoversReady}
          onTabChange={onTabChange}
          handleFilterOption={handleFilterOption}
          dropdownOptionValue={epicMoversOptionToString(optionValue)}
          tableNames={[lang.commonEpicMoversGainers(), lang.commonEpicMoversLosers()]}
          tableHeaders={[
            lang.headerAccountBreakdownSymbolName(),
            lang.commonChart(),
            lang.commonLastPrice(),
            lang.commonPercentChange(),
          ]}
          renderList={renderList}
          initialActiveTab={activeTabIndex}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isEpicMoversReady: isCallStatusReady(FundamentalCall.getEpicMovers, state.fundamental.statusByCall),
  epicMoversData: state.fundamental.epicMoversData,
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getEpicMovers: (payload: EpicMoversParamType) => dispatch(getEpicMoversCall(payload)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EpicMoversInternal);
